import React, { useState } from 'react';
import '../styles/TrainingModuleViewer.css';

export function TrainingModuleViewer({ module, onClose }) {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [moduleCompleted, setModuleCompleted] = useState(false);
  const [showVideo, setShowVideo] = useState(!!module.videoUrl);

  const lesson = module.lessons[currentLesson];
  const progress = ((currentLesson + 1) / module.lessons.length) * 100;

  const handleNextLesson = () => {
    if (currentLesson < module.lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const handlePreviousLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
    }
  };

  const handleAnswerQuestion = (questionId, optionIndex) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: optionIndex
    });
  };

  const calculateQuizScore = () => {
    let correct = 0;
    module.quiz.questions.forEach(question => {
      if (quizAnswers[question.id] === question.correct) {
        correct++;
      }
    });
    return Math.round((correct / module.quiz.questions.length) * 100);
  };

  const handleSubmitQuiz = () => {
    const score = calculateQuizScore();
    setQuizSubmitted(true);
    if (score >= module.quiz.passingScore) {
      setModuleCompleted(true);
    }
  };

  if (!showQuiz) {
    return (
      <div className="training-module-viewer slide-view">
        {/* Header */}
        <div className="tmv-header">
          <div className="tmv-header-top">
            <button className="close-btn" onClick={onClose}>✕</button>
            <div className="module-info">
              <h1>{module.title}</h1>
              <p className="module-meta">
                {module.phaseId && <span>{module.phaseName} •</span>}
                <span>⏱️ {module.duration} minutes</span>
              </p>
            </div>
            {module.videoUrl && (
              <button
                className="video-toggle-btn"
                onClick={() => setShowVideo(!showVideo)}
                title={showVideo ? 'Hide video' : 'Show video'}
              >
                {showVideo ? '🎥 Hide Video' : '🎥 Show Video'}
              </button>
            )}
          </div>
          <div className="progress-section">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="progress-text">Lesson {currentLesson + 1} of {module.lessons.length}</p>
          </div>
        </div>

        {/* Content Area - Slides and Video */}
        <div className={`tmv-content-area ${showVideo && module.videoUrl ? 'with-video' : ''}`}>
          {/* Slide Content */}
          <div className="tmv-slide-container">
            {/* Learning Objectives Slide */}
            {currentLesson === 0 && (
              <div className="slide-content objectives-slide">
                <h2>📚 Learning Objectives</h2>
                <ul className="objectives-list">
                  {module.learningObjectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Lesson Slides */}
            {currentLesson > 0 && (
              <div className="slide-content lesson-slide">
                <div className="lesson-header">
                  <h2>{lesson.title}</h2>
                  <p className="lesson-duration">⏱️ {lesson.duration} minutes</p>
                </div>
                <div className="lesson-body-slide">
                  {lesson.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="content-paragraph">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Video Player */}
          {showVideo && module.videoUrl && (
            <div className="tmv-video-container">
              {module.videoUrl.includes('youtube.com') || module.videoUrl.includes('youtu.be') ? (
                <iframe
                  className="tmv-video-player"
                  src={module.videoUrl}
                  title="Training Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  className="tmv-video-player"
                  controls
                  controlsList="nodownload"
                >
                  <source src={module.videoUrl} type="video/mp4" />
                  <source src={module.videoUrl.replace('.mp4', '.mov')} type="video/quicktime" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </div>

        {/* Slide Navigation */}
        <div className="tmv-slide-nav">
          <button
            className="nav-btn prev-btn"
            onClick={handlePreviousLesson}
            disabled={currentLesson === 0}
          >
            ← Previous
          </button>
          <div className="slide-counter">
            <span className="counter-display">{currentLesson + 1}</span>
            <span className="counter-separator">/</span>
            <span className="counter-total">{module.lessons.length}</span>
          </div>
          <button
            className="nav-btn next-btn"
            onClick={handleNextLesson}
          >
            {currentLesson === module.lessons.length - 1 ? 'Take Quiz →' : 'Next →'}
          </button>
        </div>
      </div>
    );
  }

  // Quiz View
  if (!quizSubmitted) {
    return (
      <div className="training-module-viewer quiz-view">
        <div className="tmv-header">
          <button className="close-btn" onClick={onClose}>✕</button>
          <h1>📝 Module Quiz: {module.title}</h1>
          <p className="quiz-instructions">
            Answer all questions to complete the module. You need {module.quiz.passingScore}% to pass.
          </p>
        </div>

        <div className="quiz-container">
          {module.quiz.questions.map((question, idx) => (
            <div key={question.id} className="quiz-question">
              <h3>{idx + 1}. {question.question}</h3>
              <div className="quiz-options">
                {question.options.map((option, optIdx) => (
                  <label key={optIdx} className="quiz-option">
                    <input
                      type="radio"
                      name={question.id}
                      value={optIdx}
                      checked={quizAnswers[question.id] === optIdx}
                      onChange={() => handleAnswerQuestion(question.id, optIdx)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="tmv-footer">
          <button
            className="nav-btn back-btn"
            onClick={() => setShowQuiz(false)}
          >
            ← Back to Lessons
          </button>
          <button
            className="submit-quiz-btn"
            onClick={handleSubmitQuiz}
            disabled={Object.keys(quizAnswers).length !== module.quiz.questions.length}
          >
            Submit Quiz
          </button>
        </div>
      </div>
    );
  }

  // Results View
  const score = calculateQuizScore();
  const passed = score >= module.quiz.passingScore;

  return (
    <div className="training-module-viewer results-view">
      <div className="tmv-header">
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="results-container">
        {moduleCompleted ? (
          <>
            <div className="completion-badge">🎉</div>
            <h2>Congratulations!</h2>
            <p className="completion-message">
              You have successfully completed "{module.title}"
            </p>

            <div className="score-card">
              <div className="score-display">
                <span className="score-value">{score}%</span>
                <span className="score-label">Quiz Score</span>
              </div>
              <div className="score-details">
                <p>✓ Passed (Minimum: {module.quiz.passingScore}%)</p>
                <p>✓ All lessons completed</p>
                <p>✓ Module certificate earned</p>
              </div>
            </div>

            <div className="certificate-preview">
              <h3>Certificate of Completion</h3>
              <div className="cert-content">
                <p>This is to certify that</p>
                <p className="cert-name">[User Name]</p>
                <p>has successfully completed the training module</p>
                <p className="cert-module">{module.title}</p>
                <p>in {module.phaseId ? module.phaseName : 'Professional Development'}</p>
                <p className="cert-date">Date: {new Date().toLocaleDateString()}</p>
                <p className="cert-score">Score: {score}%</p>
              </div>
            </div>

            <div className="results-actions">
              <button className="download-cert-btn">
                ⬇️ Download Certificate
              </button>
              <button className="continue-btn" onClick={onClose}>
                ✓ Complete & Return
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="failure-badge">❌</div>
            <h2>Quiz Not Passed</h2>
            <p className="failure-message">
              You need {module.quiz.passingScore}% to pass this module
            </p>

            <div className="score-card failed">
              <div className="score-display">
                <span className="score-value">{score}%</span>
                <span className="score-label">Your Score</span>
              </div>
              <div className="score-needed">
                <span className="needed-value">{module.quiz.passingScore}%</span>
                <span className="needed-label">Score Needed</span>
              </div>
            </div>

            <div className="review-section">
              <h3>Review Your Answers</h3>
              {module.quiz.questions.map((question, idx) => {
                const userAnswer = quizAnswers[question.id];
                const isCorrect = userAnswer === question.correct;
                return (
                  <div key={question.id} className={`review-item ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <p className="question-text">{idx + 1}. {question.question}</p>
                    <p className="user-answer">
                      Your answer: <strong>{question.options[userAnswer]}</strong>
                      {!isCorrect && <span> ({isCorrect ? '✓' : '✗'})</span>}
                    </p>
                    {!isCorrect && (
                      <p className="correct-answer">
                        Correct answer: <strong>{question.options[question.correct]}</strong>
                      </p>
                    )}
                    <p className="explanation">{question.explanation}</p>
                  </div>
                );
              })}
            </div>

            <div className="results-actions">
              <button className="retry-btn" onClick={() => {
                setQuizSubmitted(false);
                setQuizAnswers({});
              }}>
                🔄 Retry Quiz
              </button>
              <button className="review-lessons-btn" onClick={() => {
                setShowQuiz(false);
                setCurrentLesson(0);
              }}>
                📚 Review Lessons
              </button>
              <button className="exit-btn" onClick={onClose}>
                Exit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrainingModuleViewer;
